/**
 * Live "Product preview" rail for the New-product wizard (#272) — the
 * TradeCore artifact's right-hand summary card, ink-themed: what the listing
 * will look like, updated as the operator types. Read-only; reads the live
 * form via context and owns nothing.
 */
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { paiseToInrExact } from "../../../lib/format.ts";
import { usableEntries, type PricingEntry } from "./size-ratio.ts";

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-1.5 text-xs last:border-b-0">
      <span className="shrink-0 text-muted">{label}</span>
      <span className={`truncate text-right text-ink-2${mono ? " font-mono tabular-nums" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}

export function ProductPreviewRail({
  images,
  createAs,
  showOpeningStock = true,
}: {
  images: readonly string[];
  createAs: "draft" | "published";
  /** False on the edit wizard — opening stock is create-only. */
  showOpeningStock?: boolean;
}) {
  const { control } = useFormContext();
  const v = useWatch({ control }) as Record<string, unknown>;
  const s = (key: string) => ((v[key] as string) ?? "").trim();

  const entries = usableEntries((v.pricingEntries as PricingEntry[]) ?? []);
  const primary = entries[0];

  // Mini slider over the uploaded images — clamped so removing images can't
  // strand the index past the end.
  const [imageIdx, setImageIdx] = useState(0);
  const shownIdx = images.length ? Math.min(imageIdx, images.length - 1) : 0;

  return (
    <div className="rounded border border-line bg-paper px-4 py-4 shadow">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">
        Product preview
      </span>

      <div className="relative mt-3 flex aspect-square items-center justify-center overflow-hidden rounded border border-line bg-bg">
        {images.length ? (
          <img src={images[shownIdx]} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-muted">No image yet</span>
        )}
        <span className="absolute left-2 top-2 rounded-full bg-ink px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-paper">
          {createAs === "published" ? "Published" : "Draft"}
        </span>
        {images.length > 1 ? (
          <>
            <button
              type="button"
              aria-label="Previous image"
              className="absolute left-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-ink/70 text-paper hover:bg-ink"
              onClick={() => setImageIdx((shownIdx - 1 + images.length) % images.length)}
            >
              <ChevronLeft size={14} aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Next image"
              className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-ink/70 text-paper hover:bg-ink"
              onClick={() => setImageIdx((shownIdx + 1) % images.length)}
            >
              <ChevronRight size={14} aria-hidden />
            </button>
            <span className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 gap-1">
              {images.map((_, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={`h-1.5 w-1.5 rounded-full ${i === shownIdx ? "bg-ink" : "bg-ink/25"}`}
                />
              ))}
            </span>
          </>
        ) : null}
      </div>

      <p className="mt-3 truncate text-sm font-semibold text-ink">
        {s("name") || "Untitled product"}
      </p>
      <p className="truncate text-xs text-muted">
        {[s("category"), s("subCategory")].filter(Boolean).join(" · ") || "No category yet"}
      </p>

      <div className="mt-2">
        <Row label="SKU" value={s("sku")} mono />
        <Row
          label="Bundles"
          value={entries.length ? `${entries.length}` : ""}
          mono
        />
        <Row
          label="Price"
          // The primary entry's pack price — the figure the catalog quotes.
          value={primary ? paiseToInrExact(primary.pricePaise) : ""}
          mono
        />
        <Row label="MOQ" value={primary?.moq ? `${primary.moq} packs` : ""} mono />
        {showOpeningStock ? (
          <Row
            label="Opening stock"
            value={s("openingQty") ? `${s("openingQty")} pcs` : ""}
            mono
          />
        ) : null}
      </div>
    </div>
  );
}
